import { useCallback, useMemo, useRef, useState } from 'react'
import type { ImportEndpointItem, ImportEndpointsRequest } from '../../../../types/case-container'
import { logger } from '../../../../utils/logger'
import {
  type JsonTemplateFormat,
  type RawJsonEndpoint,
  extractRawEndpoints,
  mapAllEndpoints,
  parseJsonPreview,
} from '../json-import.utils'

export interface ImportError {
  row: number
  field: string
  message: string
}

export interface ImportResult {
  imported: number
  skipped: number
  errors: ImportError[]
}

interface UseJsonImportModalArgs {
  onImport: (data: ImportEndpointsRequest) => Promise<ImportResult | null>
}

export interface JsonImportModalState {
  file: File | null
  preview: ImportEndpointItem[]
  error: string | null
  result: ImportResult | null
  jsonText: string
  fileInputRef: React.RefObject<HTMLInputElement>
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>
  handleTextChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void
  handleImport: () => Promise<void>
}

interface ParseJsonSetters {
  setError: (v: string | null) => void
  setPreview: (v: ImportEndpointItem[]) => void
}

function createParseJson(setters: ParseJsonSetters): (text: string) => void {
  return (text: string) => {
    const outcome = parseJsonPreview(text)
    if (!outcome.ok) {
      setters.setError(outcome.error)
      setters.setPreview([])
      return
    }
    if (outcome.isTemplateFormat) {
      logger.debug(
        'CaseInfrastructureTab',
        `[JSON Import] Detected template format, found ${outcome.rawCount.toString()} records in "data"`
      )
    }
    setters.setPreview(outcome.preview)
    setters.setError(null)
  }
}

export function useJsonImportModal({ onImport }: UseJsonImportModalArgs): JsonImportModalState {
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<ImportEndpointItem[]>([])
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [jsonText, setJsonText] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const parseJson = useMemo(() => createParseJson({ setError, setPreview }), [])

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0]
      if (!selectedFile) return
      if (!selectedFile.name.endsWith('.json')) {
        setError('Please select a JSON file')
        return
      }
      setFile(selectedFile)
      setResult(null)
      try {
        const text = await selectedFile.text()
        setJsonText(text)
        parseJson(text)
      } catch {
        setError('Failed to read JSON file')
      }
    },
    [parseJson]
  )

  const handleTextChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const text = event.target.value
      setJsonText(text)
      setFile(null)
      if (text.trim()) {
        parseJson(text)
      } else {
        setPreview([])
        setError(null)
      }
    },
    [parseJson]
  )

  const handleImport = useCallback(async () => {
    if (!jsonText) return
    try {
      const parsed = JSON.parse(jsonText) as RawJsonEndpoint[] | JsonTemplateFormat
      const endpoints = mapAllEndpoints(extractRawEndpoints(parsed))
      const importResult = await onImport({ source: 'json', endpoints })
      if (importResult) setResult(importResult)
    } catch {
      setError('Failed to import JSON')
    }
  }, [jsonText, onImport])

  return {
    file,
    preview,
    error,
    result,
    jsonText,
    fileInputRef,
    handleFileChange,
    handleTextChange,
    handleImport,
  }
}
